/**
 * Small building blocks for the organisation pages (Members, Invite, Settings,
 * Attributes), in the prototype's design (docs/engineering/ORG_UI_DESIGN_REVIEW.md).
 * Colours come from orgTheme so the shell and these pages cannot drift.
 */

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { T, initials } from '@/components/shell/orgTheme'
import type { OrgRole } from '@/lib/orgApi'
import { ROLE_LABEL, type ConfirmSpec, type Status } from './orgHelpers'

// ─── Buttons ─────────────────────────────────────────────────────────────────

type BtnKind = 'primary' | 'secondary' | 'danger' | 'ghost' | 'link'

const BTN: Record<BtnKind, CSSProperties> = {
  primary:   { background: T.purple, color: '#fff', border: 'none', fontWeight: 600 },
  secondary: { background: '#fff', color: T.dark, border: `1px solid ${T.inputBorder}`, fontWeight: 500 },
  danger:    { background: '#fff', color: T.red, border: '1px solid #F1C8C3', fontWeight: 500 },
  ghost:     { background: 'transparent', color: T.red, border: 'none', fontWeight: 500 },
  link:      { background: 'transparent', color: T.purpleText, border: 'none', fontWeight: 500, padding: 0 },
}

export function Btn({ kind = 'secondary', small, onClick, disabled, children, style, type = 'button' }: {
  kind?: BtnKind; small?: boolean; onClick?: () => void; disabled?: boolean; children: ReactNode
  style?: CSSProperties; type?: 'button' | 'submit'
}) {
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      className={kind === 'primary' ? 'hover:bg-[#6A2FC4]' : kind === 'secondary' ? 'hover:bg-[#FAF9FB]' : undefined}
      style={{
        padding: small ? '6px 11px' : '9px 15px', borderRadius: 8, fontSize: small ? 13 : 14, cursor: disabled ? 'default' : 'pointer',
        whiteSpace: 'nowrap', opacity: disabled ? 0.55 : 1, display: 'inline-flex', alignItems: 'center', gap: 6,
        ...BTN[kind], ...style,
      }}>
      {children}
    </button>
  )
}

export function Toggle({ on, onChange, label, disabled }: { on: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button role="switch" aria-checked={on} aria-label={label} disabled={disabled} onClick={() => onChange(!on)}
      style={{ position: 'relative', flex: 'none', width: 40, height: 24, borderRadius: 999, border: 'none', background: on ? T.purple : '#D6D1DD', cursor: disabled ? 'default' : 'pointer', padding: 0, opacity: disabled ? 0.6 : 1 }}>
      <span style={{ position: 'absolute', top: 3, left: on ? 19 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,.2)', transition: 'left .15s' }} />
    </button>
  )
}

// ─── Inputs ──────────────────────────────────────────────────────────────────

/**
 * A labelled field. `group` renders a <div> instead of a <label> — use it for anything with
 * buttons inside (pickers with chips): a browser passes a click anywhere in a <label> on to its
 * first button, which there is a chip's ×, so clicking beside a picked chip removed it (7 Oct 2026).
 */
export function Field({ label, children, hint, group }: { label: string; children: ReactNode; hint?: ReactNode; group?: boolean }) {
  const Tag = group ? 'div' : 'label'
  return (
    <Tag style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <span style={{ fontSize: 13, fontWeight: 500, color: T.ink }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 12.5, color: T.faint }}>{hint}</span>}
    </Tag>
  )
}

export function Card({ title, children, style }: { title?: ReactNode; children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 14, padding: '26px 30px', display: 'flex', flexDirection: 'column', gap: 16, ...style }}>
      {title && <span style={{ fontSize: 16, fontWeight: 600, color: T.dark }}>{title}</span>}
      {children}
    </div>
  )
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return <div role="alert" style={{ padding: '10px 12px', borderRadius: 8, background: T.redBg, color: T.red, fontSize: 13.5 }}>{children}</div>
}

// ─── People ──────────────────────────────────────────────────────────────────

const AVATARS = ['#E9DDFB', '#DDEBFB', '#E3F3F0', '#FCEFD9', '#FBE3EC', '#EFEAF6']

export function Avatar({ id, name, imageUrl, size = 30 }: { id: string; name: string; imageUrl?: string | null; size?: number }) {
  let h = 0
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  const base: CSSProperties = { width: size, height: size, flex: 'none', borderRadius: '50%' }
  if (imageUrl) return <img src={imageUrl} alt="" style={{ ...base, objectFit: 'cover' }} />
  return (
    <span style={{ ...base, background: AVATARS[h % AVATARS.length], color: '#3B2A55', display: 'grid', placeItems: 'center', fontSize: Math.round(size * 0.38), fontWeight: 600 }}>
      {initials(name)}
    </span>
  )
}

export function StatusPill({ status, dot = true }: { status: Status; dot?: boolean }) {
  const [c, bg] = status === 'Active' ? [T.green, T.greenBg] : status === 'Invited' ? [T.amber, T.amberBg] : [T.muted, '#F1EFF4']
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 9px', borderRadius: 999, background: bg, color: c, fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap' }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: c }} />}{status}
    </span>
  )
}

/** Admin / Creator / Learner as toggle chips. `locked` roles cannot be toggled by this person. */
export function RoleChips({ value, onChange, locked = [] }: { value: OrgRole[]; onChange: (r: OrgRole[]) => void; locked?: OrgRole[] }) {
  const ALL: OrgRole[] = ['admin', 'creator', 'learner']
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {ALL.map(r => {
        const on = value.includes(r), lock = locked.includes(r)
        return (
          <button key={r} type="button" disabled={lock} aria-pressed={on}
            onClick={() => onChange(on ? value.filter(x => x !== r) : ALL.filter(x => x === r || value.includes(x)))}
            style={{
              padding: '7px 12px', borderRadius: 999, fontSize: 13.5, fontWeight: 500, cursor: lock ? 'default' : 'pointer',
              border: `1px solid ${on ? T.tintBorder : T.inputBorder}`, background: on ? T.tint : '#fff', color: on ? T.purpleText : T.ink,
              opacity: lock ? 0.5 : 1,
            }}>
            {on ? '✓ ' : '+ '}{ROLE_LABEL[r]}
          </button>
        )
      })}
    </div>
  )
}

// ─── Dialogs ─────────────────────────────────────────────────────────────────

export function Modal({ title, sub, onClose, children, footer, width = 480 }: {
  title: string; sub?: ReactNode; onClose: () => void; children: ReactNode; footer: ReactNode; width?: number
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    // Centred on the page (5 Oct 2026). margin:auto rather than align-items:center, so a
    // dialog taller than the screen still scrolls from its top instead of being cut off.
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(28,26,34,.32)', zIndex: 60, display: 'flex', padding: '24px 16px', overflowY: 'auto' }}>
      <div role="dialog" aria-modal="true" aria-label={title} onClick={e => e.stopPropagation()}
        style={{ margin: 'auto', width, maxWidth: '100%', background: '#fff', borderRadius: 16, boxShadow: '0 24px 60px rgba(40,20,70,.22)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '20px 22px 0', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 18, fontWeight: 600, color: T.dark }}>{title}</span>
          {sub && <span style={{ fontSize: 13.5, color: T.muted }}>{sub}</span>}
        </div>
        <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>
        <div style={{ padding: '14px 22px', borderTop: '1px solid #F2F0F5', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>{footer}</div>
      </div>
    </div>
  )
}

/** What a button says while it works (24 Oct 2026 — it said "Working…" for everything): its own
 *  verb, in -ing — "Delete course" → "Deleting course…", "Cancel invite" → "Cancelling invite…". */
const ING: Record<string, string> = {
  cancel: 'Cancelling', transfer: 'Transferring', stop: 'Stopping', end: 'Ending', open: 'Opening', resend: 'Resending',
  apply: 'Applying', copy: 'Copying', clear: 'Clearing', send: 'Sending', switch: 'Switching', start: 'Starting',
}
function progressive(label: string): string {
  const [first, ...rest] = label.trim().split(/\s+/)
  if (!first) return 'Saving…'
  const w = first.toLowerCase()
  const ing = ING[w] ?? (w.endsWith('e') && !w.endsWith('ee') ? first.slice(0, -1) + 'ing' : first + 'ing')
  // "Switch and start over" → "Switching…": what follows "and" is a second step, not the object.
  const tail = rest.join(' ').split(/\s+and\s+/i)[0]
  return `${ing[0].toUpperCase()}${ing.slice(1)}${tail ? ` ${tail}` : ''}…`
}

export function ConfirmDialog({ spec, onClose }: { spec: ConfirmSpec | null; onClose: () => void }) {
  const [busy, setBusy] = useState<'cta' | 'alt' | null>(null)
  if (!spec) return null
  const go = async (which: 'cta' | 'alt') => {
    setBusy(which)
    try { await (which === 'alt' ? spec.alt!.run() : spec.run()) } finally { setBusy(null); onClose() }
  }
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(28,26,34,.36)', zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div role="alertdialog" aria-modal="true" onClick={e => e.stopPropagation()}
        style={{ width: 420, maxWidth: '100%', background: '#fff', borderRadius: 16, boxShadow: '0 24px 60px rgba(40,20,70,.24)', padding: 22, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span style={{ fontSize: 17, fontWeight: 600, color: T.dark }}>{spec.title}</span>
        <span style={{ fontSize: 14, color: T.ink, lineHeight: 1.55 }}>{spec.body}</span>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
          <Btn onClick={() => { onClose(); spec.onCancel?.() }}>{spec.cancelLabel ?? 'Cancel'}</Btn>
          {spec.alt && <Btn onClick={() => void go('alt')} disabled={!!busy}>{busy === 'alt' ? progressive(spec.alt.label) : spec.alt.label}</Btn>}
          <Btn kind="primary" onClick={() => void go('cta')} disabled={!!busy} style={spec.danger ? { background: T.red } : undefined}>{busy === 'cta' ? (spec.busyLabel ?? progressive(spec.cta)) : spec.cta}</Btn>
        </div>
      </div>
    </div>
  )
}

/** Shown in place of a Manage page to someone who is not an owner or admin. */
export function ManagersOnly() {
  return (
    <div style={{ maxWidth: 560, margin: '48px auto', padding: '32px 24px', textAlign: 'center', border: `1.5px dashed ${T.inputBorder}`, borderRadius: 12, color: T.muted, fontSize: 14, lineHeight: 1.5 }}>
      Only the Owner and Admins of this organisation can see this page.
    </div>
  )
}

/** A grey shimmering block that stands in for content while it loads (7 Oct 2026). */
export function Skeleton({ w = '100%', h = 12, r = 6, style }: { w?: number | string; h?: number | string; r?: number; style?: CSSProperties }) {
  return <span aria-hidden className="lb-skeleton" style={{ display: 'block', width: w, height: h, borderRadius: r, ...style }} />
}

